import { Grid, Paper, Typography, Switch, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from '../../eventBus';
import { getAdvisorQueryParams } from './../../utils/queryParams';

const useStyles = () => ({
  rectangle: {
    borderRadius: '16px',
    padding: {
      xxs: '15px',
      sm: '21px',
    },
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
  },
  tileTitle: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '24px',
    color: '#232B35',
    letterSpacing: '0.5px',
    fontFamily: 'Poppins',
  },
  description: {
    fontWeight: 300,
    fontSize: '12px',
    lineHeight: '18px',
    color: '#232B35',
    marginTop: '5px',
    fontFamily: 'Poppins',
  },
  dataDisplay: {
    fontWeight: 600,
    fontSize: '36px',
    lineHeight: '32px',
    color: '#232B35',
    letterSpacing: '-1.5px',
    fontFamily: 'Poppins',
  },
  sliderLabel: {
    fontWeight: 300,
    fontSize: '12px',
    lineHeight: '18px',
    color: '#232B35',
    letterSpacing: '0.5px',
    fontFamily: 'Poppins',
  },
});

const OnOffSwitch = styled(Switch)(({ theme }) => ({
  width: 68, // Ancho total del switch
  height: 26, // Alto total del switch
  padding: 0,
  // Estilos para la "base" del switch (la parte clicable)
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(4px)', // Ajusta la posición inicial del thumb
    '&.Mui-checked': {
      transform: 'translateX(38px)', // Ajusta la posición cuando está encendido
      '& + .MuiSwitch-track': {
        backgroundColor: '#DBF7EB',
        opacity: 1,
        // Cambiamos el texto cuando está encendido
        '&:before': {
          content: '"On"',
          left: '8px', // Muéstralo hacia la izquierda
          right: 'auto', // Anulamos el "right"
        },
      },
    },
  },
  // Estilos para el "thumb" (círculo que se desplaza)
  '& .MuiSwitch-thumb': {
    backgroundColor: '#fff',
    width: 22,
    height: 22,
    boxShadow: 'none',
  },
  // Estilos para la pista (track)
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9E9', // color cuando está apagado
    opacity: 1,
    position: 'relative', // ¡importante para poder usar :before con position:absolute!
    '&:before': {
      content: '"Off"',
      position: 'absolute',
      right: '8px', // texto "Off" alineado a la derecha
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: 14,
      color: '#232B35',
      fontWeight: '600',
    },
  },
}));

export function MyOnOffSwitch({ checked, handleChange }) {
  return <OnOffSwitch checked={checked} onChange={handleChange} />;
}

const SwitchesTile = (props) => {
  const {
    backgroundColor = '#FFEED6',
    width = { xxs: '100%', sm: '245px' },
    height = '238px',
    marginTop = '20px',
    ids = {
      paper: 'company-own-profile-account-slide',
      upgradeLink: 'company-own-profile-account-upgrade',
      inviteContacts: '',
      accessNetwork: '',
      boardMeetings: '',
    },
  } = props;
  const classes = useStyles();
  const { accountDetails, currentPlan, isFreeTrial, isFreemium } = useAccount();
  const tier = currentPlan?.salesforceValues?.tier;

  function goToUpgradePage() {
    if (isFreemium) {
      eventBus.dispatch(eventBusValues.triggerCompanyUpgradeAction, {
        gatedAction: true,
        companyName: accountDetails ? accountDetails.companyName : '',
      });
    } else if (currentPlan.upgradeUrl) {
      window.location.replace(
        `${currentPlan.upgradeUrl}?${getAdvisorQueryParams({
          accountDetails,
          isFreeTrial,
          isFreemium,
        })}`
      );
    }
  }

  return (
    <Paper
      sx={{
        ...classes.rectangle,
        marginTop,
        backgroundColor,
        width,
        height,
      }}
      id={ids.paper}
    >
      <Typography style={classes.tileTitle}>Account</Typography>
      <Grid container>
        <Grid
          item
          xxs={12}
          style={{
            textAlign: 'left',
            paddingTop: '8px',
          }}
        >
          <Typography
            sx={{
              ...classes.dataDisplay,
              fontSize: {
                xxs: '18px',
                sm: '20px',
              },
              lineHeight: '24px',
              letterSpacing: '-1px',
            }}
          >
            {isFreemium ? 'Free Advisory Board' : 'Advisory Board'}
          </Typography>
          {isFreemium || currentPlan.upgradeUrl ? (
            <Typography
              onClick={() => goToUpgradePage()}
              id={ids.upgradeLink}
              style={{
                ...classes.description,
                cursor: 'pointer',
                color: '#3171F6',
                fontWeight: 700,
              }}
            >
              See all upgrade options
            </Typography>
          ) : null}
        </Grid>
        <Grid
          item
          xxs={12}
          style={{
            textAlign: 'left',
            paddingTop: '20px',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            id={ids.inviteContacts}
          >
            <Typography style={classes.sliderLabel}>
              Invite your contacts
            </Typography>
            <MyOnOffSwitch
              checked={true}
              handleChange={() => goToUpgradePage()}
            />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            id={ids.accessNetwork}
          >
            <Typography style={classes.sliderLabel}>
              Access our network
            </Typography>
            <MyOnOffSwitch
              checked={!isFreemium}
              handleChange={() => goToUpgradePage()}
            />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            id={ids.boardMeetings}
          >
            <Typography style={classes.sliderLabel}>Board Meetings</Typography>
            <MyOnOffSwitch
              checked={['Executive'].includes(tier)}
              handleChange={() => goToUpgradePage()}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SwitchesTile;
